import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTheme } from '@/theme';
import CompanyPage from './company/CompanyPage';
import Sites from './site/Sites';
import { HierarchicalSelectorContainer } from '@/components/common/HierarchicalSelectorContainer';
import { useCompanySiteScope } from '@/hooks/useCompanySiteScope';
import { GuideButton } from '@/features/guide';

type TabId = 'company' | 'site';

const TABS: { id: TabId; label: string }[] = [
  { id: 'company', label: 'Company' },
  { id: 'site', label: 'Site' },
];

const ManagementPage: React.FC = () => {
  const { theme } = useTheme();
  const { isSiteManager, isCompanyManager } = useCompanySiteScope();
  const [searchParams] = useSearchParams();
  const initialTab: TabId = searchParams.get('tab') === 'site' ? 'site' : 'company';
  const [activeTab, setActiveTab] = useState<TabId>(initialTab);
  const [hoveredTab, setHoveredTab] = useState<TabId | null>(null);

  React.useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'site') setActiveTab('site');
    else if (tabParam === 'company') setActiveTab('company');
  }, [searchParams]);

  // SiteManager(role_id=3)만: 탭 없이 Site 페이지만 표시
  if (isSiteManager && !isCompanyManager) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing[2],
            padding: `${theme.spacing[4]}px ${theme.spacing[7]}px`,
            flexShrink: 0,
          }}
        >
          <HierarchicalSelectorContainer overrideDepth="site" disableSiteAll />
          <GuideButton guideKey="siteDetail" iconOnly iconSize={22} />
        </div>
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <Sites />
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      {/* Tab Header */}
      <div
        data-tour="company-intro"
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          paddingTop: theme.spacing[7],
          paddingLeft: theme.spacing[6],
          flexShrink: 0,
        }}
      >
        {TABS.map(tab => {
          const isActive = activeTab === tab.id;
          const isHovered = hoveredTab === tab.id;

          let indicatorColor = theme.colors.border.interactive.default;
          if (isActive) indicatorColor = theme.colors.border.interactive.selected;
          else if (isHovered) indicatorColor = theme.colors.border.interactive.hover;

          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              onMouseEnter={() => setHoveredTab(tab.id)}
              onMouseLeave={() => setHoveredTab(null)}
              style={{
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: `${theme.spacing[2]}px ${theme.spacing[6]}px`,
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                color: isActive ? theme.colors.text.primary : theme.colors.text.disabled,
                fontSize: isActive ? theme.fontSize.md : theme.fontSize.sm,
                fontWeight: theme.fontWeight.medium,
                lineHeight: theme.lineHeight.normal,
                transition: 'color 0.2s ease',
              }}
            >
              <span>{tab.label}</span>
              <div
                style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: '2px',
                  background: indicatorColor,
                  transition: 'background 0.2s ease',
                }}
              />
            </button>
          );
        })}

        {/* 현재 탭에 맞는 가이드 */}
        <div style={{ display: 'flex', alignItems: 'center', marginLeft: theme.spacing[2] }}>
          <GuideButton
            guideKey={activeTab === 'company' ? 'companyDetail' : 'siteDetail'}
            iconOnly
            iconSize={22}
          />
        </div>
      </div>

      {/* Selector */}
      <div
        style={{
          padding: `${theme.spacing[4]}px ${theme.spacing[7]}px`,
          flexShrink: 0,
        }}
      >
        <HierarchicalSelectorContainer
          overrideDepth={activeTab === 'company' ? 'company' : 'site'}
          disableSiteAll={activeTab === 'site'}
        />
      </div>

      {/* Tab Content */}
      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {activeTab === 'company' ? <CompanyPage /> : <Sites />}
      </div>
    </div>
  );
};

export default ManagementPage;
